import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Search,
  RefreshCw,
  Users,
  FileText,
  Calendar,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

import RecordDetails from '../components/RecordDetails.jsx';
import Header from '../components/Header.jsx';
import ScheduleCalendar from '../components/ScheduleCalendar.jsx';
import Pagination from '../components/Pagination.jsx';

/**
 * Provider Dashboard
 * Shows only the patients (records) where record.provider === loggedInUser.fullName (or username)
 */
const ProviderDashboard = () => {
  const { user, logout } = useAuth();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // shared with list & calendar
  const [selectedRecord, setSelectedRecord] = useState(null);

  // 'records' | 'schedule'
  const [viewMode, setViewMode] = useState('records');

  useEffect(() => {
    if (!user) return;
    if (viewMode !== 'records') return;

    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, limit, search, viewMode]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await api.get('/records', {
        params: {
          page,
          limit,
          search: search.trim() || undefined,
        },
      });

      const { data, total: t, limit: l } = res.data || {};
      setRecords(data || []);
      setTotal(t || 0);
      if (l) setLimit(l);
    } catch (err) {
      console.error('Failed to fetch provider records', err);
      const msg = err.response?.data?.message || err.message;
      setError(`Failed to load patients: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecord = async (id, data) => {
    const res = await api.put(`/records/${id}`, data);
    const updated = res.data;

    // keep list in sync when coming from records tab
    setRecords((prev) => prev.map((r) => (r._id === id ? updated : r)));
    return updated;
  };

  const handleBackFromDetail = () => {
    setSelectedRecord(null);
    if (viewMode === 'records') {
      fetchRecords();
    }
    // if from schedule, we don't need to re-fetch here
  };

  const handleUpdateUser = (updatedUser) => {
    console.log('User updated:', updatedUser);
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading user data...
      </div>
    );
  }

  const filterName = user.fullName || user.username || 'Unknown';

  const navItems = [
    { label: 'My Patients', icon: <Users className="w-4 h-4" />, mode: 'records' },
    { label: 'Schedule', icon: <Calendar className="w-4 h-4" />, mode: 'schedule' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header
        user={user}
        onLogout={logout}
        api={api}
        onUpdateUser={handleUpdateUser}
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* When a record is selected (from list OR calendar), show details */}
        {selectedRecord ? (
          <RecordDetails
            record={selectedRecord}
            user={user}
            api={api}
            onBack={handleBackFromDetail}
            onSave={handleSaveRecord}
            onAssign={null}      // hide collector assignment for provider
            collectors={[]}
          />
        ) : (
          <>
            {viewMode === 'records' && (
              <>
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <Users className="w-8 h-8 text-indigo-600" />
                    My Patients
                  </h1>
                  <p className="mt-2 text-gray-600">
                    Viewing records for Provider:{' '}
                    <span className="font-semibold text-indigo-600">
                      {filterName}
                    </span>
                  </p>
                </div>

                {/* Search + refresh */}
                <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="relative w-full sm:max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Search by Patient Name, Case Status..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                  <button
                    onClick={fetchRecords}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <RefreshCw
                      className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                    />
                    Refresh
                  </button>
                </div>

                {/* List / states */}
                {error && (
                  <div className="bg-red-50 text-red-700 p-4 rounded-md flex items-center gap-2 mb-4">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                  </div>
                )}

                {loading ? (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
                  </div>
                ) : records.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-12 text-center">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No patients found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No records match provider "<strong>{filterName}</strong>".
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                        {records.map((record) => (
                          <li key={record._id}>
                            <button
                              type="button"
                              onClick={() => setSelectedRecord(record)}
                              className="block w-full text-left hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out"
                            >
                              <div className="px-4 py-4 sm:px-6">
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-col">
                                    <p className="text-lg font-semibold text-indigo-600 truncate">
                                      {record.ptName}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      DOB:{' '}
                                      {record.dob
                                        ? new Date(
                                            record.dob
                                          ).toLocaleDateString()
                                        : 'N/A'}
                                    </p>
                                  </div>
                                  <div className="ml-2 flex-shrink-0 flex">
                                    <span
                                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                      ${
                                        record.caseStatus === 'SETTLED'
                                          ? 'bg-green-100 text-green-800'
                                          : record.caseStatus === 'CIC PENDING'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : 'bg-gray-100 text-gray-800'
                                      }`}
                                    >
                                      {record.caseStatus || 'No Status'}
                                    </span>
                                  </div>
                                </div>
                                <div className="mt-2 sm:flex sm:justify-between">
                                  <div className="sm:flex sm:space-x-6">
                                    <p className="flex items-center text-sm text-gray-500">
                                      <FileText className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                      Claim #: {record.claimNo?.[0]?.value || 'N/A'}
                                    </p>
                                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                      <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                      Hearing:{' '}
                                      {record.hearingDate
                                        ? new Date(
                                            record.hearingDate
                                          ).toLocaleDateString()
                                        : 'None'}
                                    </p>
                                  </div>
                                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                    <CheckCircle
                                      className={`flex-shrink-0 mr-1.5 h-4 w-4 ${
                                        record.outstanding <= 0
                                          ? 'text-green-500'
                                          : 'text-red-400'
                                      }`}
                                    />
                                    Balance: $
                                    {record.outstanding?.toLocaleString() ||
                                      '0.00'}
                                  </div>
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Pagination
                      page={page}
                      setPage={setPage}
                      limit={limit}
                      setLimit={setLimit}
                      total={total}
                    />
                  </>
                )}
              </>
            )}

            {viewMode === 'schedule' && (
              <div className="mt-4">
                <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Calendar className="w-6 h-6 text-indigo-600" />
                  My Schedule
                </h2>

                {/* IMPORTANT: pass onRecordSelect, so events can open RecordDetails */}
                <ScheduleCalendar
                  user={user}
                  api={api}
                  onRecordSelect={setSelectedRecord}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProviderDashboard;